import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Board } from '@/chess/board';
import { StockfishEngine, isStockfishInitError } from '@/chess/engine';
import { logError } from '@/api/errorLog';
import { useAwardXp } from '@/api/xp';
import {
  FixedNav,
  Caption,
  Button,
  ProgressRing,
  PulseDot,
  SessionTimer,
  XpFloater,
  KidHint,
  ErrorMessageWithDebug,
} from '@/ui';
import { gameAutopsyApi, type GameDetail, type MistakePayload, type StoreMistakesResponse } from './api';
import { AUTOPSY_DEPTH, runAutopsy } from './autopsyRunner';
import { AutopsyErrorBoundary } from './ErrorBoundary';
import { buildAutopsyDebugString } from './buildAutopsyDebugString';

/**
 * Détection durcie post-vague 1 MVP2.5 : on délègue maintenant à
 * `isStockfishInitError` exporté par `@/chess/engine`. Celui-ci préfère
 * `err instanceof StockfishInitError` (classe d'erreur typée) puis tombe
 * en fallback heuristique string-based pour les erreurs imprévues — pas
 * de divergence entre call-sites.
 */

const STOCKFISH_INIT_KID_MSG =
  'Stockfish ne se charge pas sur ce navigateur — réessaie sur ton ordi.';

/**
 * Autopsy — détail partie + analyse Stockfish navigateur (port V2).
 *
 * Préserve toute la mécanique runner Stockfish héritée du MVP1.1 :
 *  - chargement Stockfish WASM lazy via StockfishEngine
 *  - runAutopsy() coup par coup, depth 16 NNUE
 *  - storeMistakes() POST /games/{id}/mistakes après analyse
 *  - lookupFen() pour détecter sortie de répertoire
 *  - cleanup engine.destroy() en useEffect
 *
 * Refonte visuelle = 3 run states (idle/running/done/error) + layout 2 colonnes
 * (gauche: status duo + run panel + out-of-book card avec lien Opening Lab,
 * droite: Board + mistakes list).
 */

type RunState =
  | { kind: 'idle' }
  | { kind: 'running'; current: number; total: number; mistakesSoFar: number }
  | {
      kind: 'done';
      mistakes: MistakePayload[];
      outOfBookPly: number | null;
      report: StoreMistakesResponse | null;
      /**
       * Présent uniquement quand l'état `done` est restauré depuis la DB au
       * remount (fix bug autopsy-not-persisted-on-back-nav). Permet d'afficher
       * la date du run précédent dans le summary et de masquer le burst XP
       * (XP déjà awardé lors du run d'origine, idempotence backend 1h).
       */
      restoredAt?: string;
    }
  | { kind: 'error'; message: string };

const CADENCE_LABEL: Record<GameDetail['cadence_bucket'], string> = {
  bullet: 'Bullet',
  blitz: 'Blitz',
  rapid: 'Rapid',
  classical: 'Classique',
  correspondence: 'Corres.',
};

const RESULT_BADGE = {
  win: { label: 'Victoire', color: 'var(--volt-500)' },
  loss: { label: 'Défaite', color: 'var(--coral-500)' },
  draw: { label: 'Nulle', color: 'var(--ink-300)' },
};

export function AutopsyPage(): JSX.Element {
  // Banner amber non bloquant si crossOriginIsolated=false (T8 MVP2.5).
  // Typique tablette Safari iPad où COOP/COEP ne s'appliquent pas correctement
  // → Stockfish tombe en single-thread (lent mais ok). Info, pas erreur.
  const [showCsrBanner, setShowCsrBanner] = useState(false);
  const [csrBannerDismissed, setCsrBannerDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // `crossOriginIsolated` est défini sur window dans tous les navigateurs
    // modernes ; false par défaut si headers COOP/COEP pas appliqués.
    if (window.crossOriginIsolated === false) {
      setShowCsrBanner(true);
    }
  }, []);

  return (
    <>
      {showCsrBanner && !csrBannerDismissed && (
        <CrossOriginBanner onDismiss={() => setCsrBannerDismissed(true)} />
      )}
      <AutopsyErrorBoundary>
        <AutopsyRunner />
      </AutopsyErrorBoundary>
    </>
  );
}

function AutopsyRunner(): JSX.Element {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const id = Number(gameId);

  const detailQ = useQuery({
    queryKey: ['game', id],
    queryFn: () => gameAutopsyApi.fetch(id),
    enabled: Number.isFinite(id) && id > 0,
  });

  /**
   * Fix bug autopsy-not-persisted-on-back-nav (2026-05-20) : on lit l'état
   * d'autopsie persisté côté backend pour réhydrater `runState='done'` au
   * remount (navigation aller-retour, refresh). Sans ça, le state local
   * React était perdu et Léon voyait à nouveau l'écran « idle » comme si
   * l'analyse n'avait jamais eu lieu.
   */
  const autopsyQ = useQuery({
    queryKey: ['game-autopsy', id],
    queryFn: () => gameAutopsyApi.fetchAutopsy(id),
    enabled: Number.isFinite(id) && id > 0,
    // Refetch désactivé : l'état est figé tant que Léon ne relance pas
    // d'analyse (qui invalide la query manuellement à la fin de `start()`).
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: Infinity,
  });

  const [runState, setRunState] = useState<RunState>({ kind: 'idle' });
  const [sessionXp, setSessionXp] = useState(0);
  const [xpBurst, setXpBurst] = useState<{ value: number; key: number } | null>(null);
  const engineRef = useRef<StockfishEngine | null>(null);
  const sessionStartRef = useRef<number>(Date.now());

  // Barème XP : +30 par analyse complétée (action longue et pédagogiquement
  // riche, cf. docs/XP_BAREME.md). Une seule fois à la transition vers `done`.
  const XP_PER_AUTOPSY = 30;

  // T5 MVP2.5 : persiste l'XP backend. On garde `sessionXp` local en
  // parallèle (anti-cible WAKEUP : ne pas le supprimer avant validation hook
  // backend en prod). Idempotence backend 1h évite les doubles awards si
  // l'analyse est relancée immédiatement.
  const awardXp = useAwardXp();

  const storeMutation = useMutation({
    mutationFn: (mistakes: MistakePayload[]) => gameAutopsyApi.storeMistakes(id, mistakes),
  });

  useEffect(() => {
    return () => {
      engineRef.current?.destroy();
      engineRef.current = null;
    };
  }, []);

  /**
   * Fix bug 2026-05-20 — restauration de l'état `done` depuis la DB au mount.
   *
   * Ne remplace JAMAIS un état non-idle (running / done déjà affiché / error)
   * pour éviter de stomp un run en cours. Le `last_analyzed_at` est le marqueur
   * unique « cette partie a été analysée », même si `mistakes` est vide
   * (partie propre). Les `pattern_tags: null` du backend sont normalisés en
   * `undefined` côté front pour rester cohérent avec le type `MistakePayload`
   * (champ optionnel).
   */
  useEffect(() => {
    if (runState.kind !== 'idle') return;
    const data = autopsyQ.data;
    if (!data || data.last_analyzed_at === null) return;
    const restoredMistakes: MistakePayload[] = data.mistakes.map((m) => {
      const base: MistakePayload = {
        ply: m.ply,
        fen_before: m.fen_before,
        move_played: m.move_played,
        move_expected: m.move_expected,
        eval_drop_cp: m.eval_drop_cp,
      };
      if (m.pattern_tags) base.pattern_tags = m.pattern_tags;
      if (m.time_pressure) base.time_pressure = true;
      return base;
    });
    setRunState({
      kind: 'done',
      mistakes: restoredMistakes,
      outOfBookPly: data.out_of_book_ply,
      report: data.report,
      restoredAt: data.last_analyzed_at,
    });
  }, [autopsyQ.data, runState.kind]);

  async function start(): Promise<void> {
    if (!detailQ.data) return;
    setRunState({ kind: 'running', current: 0, total: 0, mistakesSoFar: 0 });
    try {
      const engine = new StockfishEngine();
      await engine.init();
      engineRef.current = engine;

      const { mistakes, outOfBookPly } = await runAutopsy(
        detailQ.data.pgn,
        detailQ.data.color,
        engine,
        (p) =>
          setRunState({
            kind: 'running',
            current: p.current,
            total: p.total,
            mistakesSoFar: p.mistakesSoFar,
          }),
        AUTOPSY_DEPTH,
        { lookupFen: (fen) => gameAutopsyApi.lookupFen(fen) },
      );

      engine.destroy();
      engineRef.current = null;

      // Fix bug 2026-05-20 — TOUJOURS appeler storeMistakes (même avec []) :
      // le backend marque `games.last_analyzed_at = NOW()` à chaque appel,
      // marqueur unique de « cette partie a été analysée » utilisé pour
      // réhydrater l'état `done` au remount du composant.
      const report = await storeMutation.mutateAsync(mistakes);
      setRunState({ kind: 'done', mistakes, outOfBookPly, report });
      // Invalide la query autopsy pour qu'un futur remount lise le nouvel
      // état persisté (réhydratation idempotente).
      void queryClient.invalidateQueries({ queryKey: ['game-autopsy', id] });
      // Fix idempotence permanente 2026-05-17 : on n'affiche le +30 XP local
      // qu'APRÈS confirmation backend (`data.awarded === true`). Sinon Léon
      // re-analyse la même partie et voit le burst alors que le backend
      // skip → divergence au refresh. Backend idempotence permanente sur
      // (user, module, source_ref). On garde le `mutate` (pas
      // `mutateAsync`) pour ne pas bloquer le rendu — l'UX est légèrement
      // décalée (~100ms) mais cohérente.
      awardXp.mutate(
        {
          module: 'autopsy',
          source_ref: `game_${id}`,
          xp_amount: XP_PER_AUTOPSY,
        },
        {
          onSuccess: (data) => {
            if (data.awarded) {
              setSessionXp((x) => x + XP_PER_AUTOPSY);
              setXpBurst({ value: XP_PER_AUTOPSY, key: Date.now() });
            }
          },
        },
      );
    } catch (err) {
      const rawMessage = err instanceof Error ? err.message : 'Erreur inconnue';
      // Si l'erreur sent l'init Stockfish, on bascule sur message kid-friendly FR
      // (T8 MVP2.5, bug A4 — typique Safari iPad sans crossOriginIsolated).
      const kidMessage = isStockfishInitError(err) ? STOCKFISH_INIT_KID_MSG : rawMessage;
      setRunState({ kind: 'error', message: kidMessage });
      // Log backend pour trace (errorLog.logError rate-limited 30/min/IP + cap
      // 50/session côté front). Best-effort.
      void logError({
        message: `[autopsy:start] ${rawMessage}`,
        error: err,
        context: {
          module: 'autopsy',
          extra: {
            gameId: id,
            crossOriginIsolated:
              typeof window !== 'undefined' ? window.crossOriginIsolated === true : null,
            stockfishInitGuess: isStockfishInitError(err),
          },
        },
      });
      engineRef.current?.destroy();
      engineRef.current = null;
    }
  }

  if (detailQ.isPending) {
    return <FrameMessage>Chargement de la partie…</FrameMessage>;
  }
  if (detailQ.isError || !detailQ.data) {
    return <FrameMessage>Partie introuvable.</FrameMessage>;
  }

  const g = detailQ.data;
  const resultMeta = RESULT_BADGE[g.result];
  const progressPct =
    runState.kind === 'running' && runState.total > 0
      ? Math.round((runState.current / runState.total) * 100)
      : runState.kind === 'done'
        ? 100
        : 0;

  const chips = [
    { label: 'Partie', value: `#${g.id}`, color: 'var(--coral-500)' },
    { label: resultMeta.label, value: g.color === 'white' ? '◻' : '◼', color: resultMeta.color },
  ];

  return (
    <>
      <FixedNav chips={chips} />
      <main className="mx-auto max-w-[1280px] px-6 py-6 flex flex-col gap-5">
        {/* Header condensé */}
        <header className="flex flex-col gap-2">
          <div className="flex items-center gap-2 text-body-sm">
            <Link
              to="/game-autopsy"
              className="text-ink-300 hover:text-paper-50 transition-all duration-150"
            >
              ← Game Autopsy
            </Link>
            <span className="font-mono text-[11px] text-ink-300">· partie #{g.id}</span>
          </div>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="font-display text-heading-1 text-paper-50">
                {g.opening_name ?? 'Ouverture inconnue'}{' '}
                <span className="font-medium text-ink-300 text-lg">
                  ({g.opening_eco ?? '—'})
                </span>
              </h1>
              <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                <span
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-mono"
                  style={{ color: resultMeta.color, borderColor: resultMeta.color }}
                >
                  <span
                    aria-hidden="true"
                    className="size-1.5 rounded-full"
                    style={{ background: resultMeta.color }}
                  />
                  {resultMeta.label}
                </span>
                <span className="font-mono text-xs text-ink-300">
                  {CADENCE_LABEL[g.cadence_bucket]} · {g.time_control} ·{' '}
                  {g.color === 'white' ? 'Blancs' : 'Noirs'} ·{' '}
                  <span className="text-paper-50">{g.rating_self}</span>{' '}
                  vs{' '}
                  <span
                    style={{
                      color:
                        g.rating_opp > g.rating_self ? 'var(--amber-500)' : 'var(--ink-300)',
                    }}
                  >
                    {g.rating_opp}
                  </span>
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4 font-mono text-xs">
              <div className="flex flex-col items-end gap-0.5">
                <Caption>Session</Caption>
                <SessionTimer startedAtMs={sessionStartRef.current} />
              </div>
              <span className="text-ink-700">·</span>
              <div className="flex flex-col items-end gap-0.5">
                <Caption>Jouée</Caption>
                <span className="text-paper-50 font-medium">{formatDate(g.played_at)}</span>
              </div>
              <span className="text-ink-700">·</span>
              <div className="flex flex-col items-end gap-0.5">
                <Caption>XP session</Caption>
                <span className="font-mono text-sm text-acid-500 font-medium tabular-nums">
                  +{sessionXp} pts
                </span>
              </div>
            </div>
          </div>
        </header>

        <KidHint tone="coral" title="Pourquoi ce drill ?">
          Stockfish analyse ta partie coup par coup et te montre où tu as
          décroché. Tes erreurs deviennent des cartes de révision automatiques
          à revoir demain dans Opening Lab.
        </KidHint>

        <section className="grid grid-cols-1 lg:grid-cols-[minmax(280px,360px)_1fr] gap-6 items-start">
          {/* LEFT */}
          <aside className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
              <PliesTile
                state={runState}
              />
              <DepthTile />
            </div>

            {runState.kind === 'idle' && (
              <GlassCard accent="coral" icon="🔍" label="En attente">
                <p className="text-body-sm text-paper-50 leading-relaxed">
                  L'analyse va dérouler la partie coup par coup et évaluer chacun de tes coups à
                  profondeur {AUTOPSY_DEPTH}.
                </p>
                <p className="mt-2 text-xs text-ink-300 leading-relaxed">
                  Temps estimé : ~90 s pour une partie Rapid. Stockfish tourne dans ton
                  navigateur, pas sur le serveur.
                </p>
                <div className="mt-3">
                  <Button variant="primary" accent="coral" onClick={start}>
                    ⚡ Lancer l'autopsie
                  </Button>
                </div>
              </GlassCard>
            )}

            {runState.kind === 'running' && (
              <GlassCard accent="coral" icon="◉" label="Analyse en cours">
                <div className="flex items-center gap-4">
                  <ProgressRing
                    value={progressPct}
                    size={88}
                    thickness={6}
                    color="var(--coral-500)"
                  />
                  <div className="flex-1 flex flex-col gap-1 font-mono text-xs text-ink-300">
                    <span>
                      coup <span className="text-paper-50">{runState.current}</span> /{' '}
                      {runState.total}
                    </span>
                    <span>
                      erreurs captées ·{' '}
                      <span className="text-coral-500">{runState.mistakesSoFar}</span>
                    </span>
                    <span>
                      ≈{' '}
                      <span className="text-paper-50">
                        {Math.max(0, 90 - Math.round(progressPct * 0.9))}
                      </span>
                      s restantes
                    </span>
                  </div>
                </div>
              </GlassCard>
            )}

            {runState.kind === 'done' && (
              <DoneSummary
                state={runState}
                onDrillRepertoire={() => {
                  const slug = g.opening_name
                    ?.toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-|-$/g, '');
                  if (slug) navigate(`/opening-lab/${slug}`);
                  else navigate('/opening-lab');
                }}
              />
            )}

            {runState.kind === 'error' && (
              <GlassCard accent="coral" icon="⚠" label="Erreur autopsie">
                <p className="text-body-sm text-coral-500 leading-relaxed">{runState.message}</p>
                <ErrorMessageWithDebug
                  className="mt-2 text-xs"
                  message="Recharge la page. Si ça ne marche toujours pas, préviens Valentin."
                  debug={buildAutopsyDebugString()}
                />
              </GlassCard>
            )}
          </aside>

          {/* RIGHT — board + mistakes */}
          <div className="flex flex-col gap-3 relative">
            {xpBurst && (
              <XpFloater
                value={xpBurst.value}
                tokenKey={xpBurst.key}
                caption="Autopsie complétée"
                className="top-0 right-4"
              />
            )}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <PulseDot
                  color={
                    runState.kind === 'done'
                      ? 'var(--volt-500)'
                      : runState.kind === 'running'
                        ? 'var(--coral-500)'
                        : runState.kind === 'error'
                          ? 'var(--coral-500)'
                          : 'var(--ink-300)'
                  }
                  size={7}
                />
                <Caption>
                  {runState.kind === 'idle'
                    ? 'En attente'
                    : runState.kind === 'running'
                      ? 'Analyse'
                      : runState.kind === 'done'
                        ? 'Terminée'
                        : 'Erreur'}
                </Caption>
              </div>
              <span className="font-mono text-[11px] text-ink-300">
                partie #{g.id}
              </span>
            </div>

            <Board orientation={g.color} interactive={false} />

            {runState.kind === 'done' && runState.mistakes.length > 0 && (
              <MistakesList
                mistakes={runState.mistakes}
                outOfBookPly={runState.outOfBookPly}
              />
            )}
          </div>
        </section>
      </main>
    </>
  );
}

// ─── Sous-composants ──────────────────────────────────────────────────────

function PliesTile({ state }: { state: RunState }): JSX.Element {
  const current =
    state.kind === 'running'
      ? state.current
      : state.kind === 'done'
        ? state.mistakes.length > 0
          ? state.mistakes[state.mistakes.length - 1]!.ply
          : 0
        : 0;
  const total = state.kind === 'running' ? state.total : 0;
  const pct =
    state.kind === 'done'
      ? 100
      : total > 0
        ? Math.round((current / total) * 100)
        : 0;
  return (
    <div className="flex flex-col gap-1.5 p-3 rounded-lg border border-ink-700 glass">
      <Caption>Coups analysés</Caption>
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-paper-50 leading-none text-[22px] font-medium">
          {state.kind === 'done' ? '✓' : current}
        </span>
        {total > 0 && (
          <span className="font-mono text-xs text-ink-300">/ {total}</span>
        )}
      </div>
      <div className="h-1 bg-ink-700 rounded-full overflow-hidden">
        <div
          className="h-full bg-coral-500 transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function DepthTile(): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5 p-3 rounded-lg border border-ink-700 glass">
      <Caption>Profondeur</Caption>
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-paper-50 leading-none text-[22px] font-medium">
          d{AUTOPSY_DEPTH}
        </span>
        <span className="font-mono text-xs text-ink-300">Stockfish 16 NNUE</span>
      </div>
      <span className="font-mono text-[11px] text-ink-300">dans ton navigateur</span>
    </div>
  );
}

function DoneSummary({
  state,
  onDrillRepertoire,
}: {
  state: Extract<RunState, { kind: 'done' }>;
  onDrillRepertoire: () => void;
}): JSX.Element {
  const { mistakes, outOfBookPly, report, restoredAt } = state;
  const restoredLabel = restoredAt ? formatRestoredAt(restoredAt) : null;
  if (mistakes.length === 0 && outOfBookPly === null) {
    return (
      <GlassCard accent="volt" icon="✓" label="Analyse terminée">
        <p className="text-body-sm text-paper-50 leading-relaxed">
          Aucun écart significatif (≥ 50 cp) sur tes coups. Partie propre — on classe sans
          carte.
        </p>
        {restoredLabel && (
          <p className="mt-2 font-mono text-[11px] text-ink-300">
            ↳ analyse persistée · {restoredLabel}
          </p>
        )}
      </GlassCard>
    );
  }
  const by = report?.by_category ?? { blunder: 0, mistake: 0, inaccuracy: 0 };
  const oobMistakes = mistakes.filter((m) => m.pattern_tags?.includes('out_of_book')).length;
  return (
    <>
      <GlassCard accent="coral" icon="🎯" label="Analyse terminée">
        <p className="text-body-sm text-paper-50 leading-relaxed">
          <strong className="text-coral-500">{mistakes.length}</strong> coup(s) à revoir ·{' '}
          <strong className="text-violet-500">{oobMistakes}</strong> hors répertoire
        </p>
        <div className="mt-3 grid grid-cols-3 gap-2">
          <CategoryStat label="Gaffe" count={by.blunder ?? 0} color="var(--coral-500)" threshold="≥ 150cp" />
          <CategoryStat label="Erreur" count={by.mistake ?? 0} color="var(--amber-500)" threshold="80-150cp" />
          <CategoryStat label="Imprécision" count={by.inaccuracy ?? 0} color="var(--ink-300)" threshold="30-80cp" />
        </div>
        <p className="mt-3 font-mono text-[11px] text-ink-300">
          →{' '}
          <span className="text-volt-500">{report?.cards_generated ?? 0}</span>{' '}
          carte(s) de révision créée(s) · dues demain
          {restoredLabel && (
            <>
              <br />↳ analyse persistée · {restoredLabel}
            </>
          )}
        </p>
      </GlassCard>

      {outOfBookPly !== null && (
        <GlassCard accent="violet" icon="📚" label="Sortie de répertoire">
          <p className="text-body-sm text-paper-50 leading-relaxed">
            Tu as quitté ton répertoire au coup{' '}
            <span className="font-mono text-violet-500">{outOfBookPly}</span>. Les erreurs
            au-delà sont marquées « hors livre » et priorisées en révision.
          </p>
          <div className="mt-3">
            <Button variant="primary" accent="violet" onClick={onDrillRepertoire}>
              📖 Travaille cette ligne dans Opening Lab
            </Button>
          </div>
        </GlassCard>
      )}
    </>
  );
}

function CategoryStat({
  label,
  count,
  color,
  threshold,
}: {
  label: string;
  count: number;
  color: string;
  threshold: string;
}): JSX.Element {
  return (
    <div className="p-2 rounded-md bg-ink-900/60 flex flex-col gap-0.5">
      <span className="font-mono text-[10px] text-ink-300 uppercase tracking-wider">{label}</span>
      <span
        className="font-mono leading-none text-[18px] font-medium"
        style={{ color }}
      >
        {count}
      </span>
      <span className="font-mono text-[10px] text-ink-300">{threshold}</span>
    </div>
  );
}

function MistakesList({
  mistakes,
  outOfBookPly,
}: {
  mistakes: MistakePayload[];
  outOfBookPly: number | null;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-ink-700 glass glass-coral">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-ink-700">
        <Caption color="coral">Coups à revoir</Caption>
        <span className="ml-auto font-mono text-[11px] text-ink-300">
          tri par perte d'éval
        </span>
      </div>
      <ol className="list-none p-0 m-0">
        {[...mistakes]
          .sort((a, b) => b.eval_drop_cp - a.eval_drop_cp)
          .map((m, i) => {
            const isOutOfBook = outOfBookPly !== null && m.ply >= outOfBookPly;
            const category =
              m.eval_drop_cp >= 150
                ? { label: 'gaffe', color: 'var(--coral-500)' }
                : m.eval_drop_cp >= 80
                  ? { label: 'erreur', color: 'var(--amber-500)' }
                  : { label: 'imprécision', color: 'var(--ink-300)' };
            return (
              <li
                key={`${m.ply}-${i}`}
                className="flex items-center gap-3 px-4 py-2.5 border-t border-ink-700/40 hover:bg-paper-50/[0.02] transition-colors duration-150"
              >
                <span className="font-mono text-[11px] text-ink-300 w-12 flex-none">
                  coup {m.ply}
                </span>
                <span
                  className="px-1.5 py-0.5 rounded font-display text-[10px] uppercase tracking-wider flex-none"
                  style={{ color: category.color, backgroundColor: `${category.color}15` }}
                >
                  {category.label}
                </span>
                <span className="flex-1 flex items-baseline gap-2 font-mono text-xs flex-wrap">
                  <span className="text-ink-300">joué</span>
                  <strong style={{ color: category.color }}>{m.move_played}</strong>
                  <span className="text-ink-300">· attendu</span>
                  <strong className="text-volt-500">{m.move_expected}</strong>
                </span>
                {isOutOfBook && (
                  <span className="px-1.5 py-0.5 rounded font-mono text-[10px] text-violet-500 border border-violet-500/40 bg-violet-500/10 flex-none">
                    hors livre
                  </span>
                )}
                <span
                  className="font-mono text-xs whitespace-nowrap flex-none font-medium"
                  style={{ color: category.color }}
                >
                  −{m.eval_drop_cp} cp
                </span>
              </li>
            );
          })}
      </ol>
    </div>
  );
}

function GlassCard({
  accent,
  icon,
  label,
  children,
}: {
  accent: 'coral' | 'violet' | 'volt' | 'amber';
  icon: string;
  label: string;
  children: ReactNode;
}): JSX.Element {
  const accentColor = {
    coral: 'var(--coral-500)',
    violet: 'var(--violet-500)',
    volt: 'var(--volt-500)',
    amber: 'var(--amber-500)',
  }[accent];
  // String littéral (pas template) : Tailwind purge supprime les classes
  // construites via `glass-${accent}` faute de pouvoir les détecter.
  const glassClass = {
    coral: 'glass glass-coral',
    violet: 'glass glass-violet',
    volt: 'glass glass-volt',
    amber: 'glass glass-amber',
  }[accent];
  return (
    <div className={`relative p-4 rounded-lg ${glassClass}`}>
      <span
        aria-hidden="true"
        className="absolute top-3 right-3 inline-flex items-center justify-center w-6 h-6 rounded border font-mono text-xs"
        style={{
          color: accentColor,
          borderColor: `${accentColor}33`,
          backgroundColor: `${accentColor}10`,
        }}
      >
        {icon}
      </span>
      <div className="mr-9">
        <span
          className="block text-caption uppercase mb-2"
          style={{ color: accentColor }}
        >
          {label}
        </span>
        {children}
      </div>
    </div>
  );
}

function FrameMessage({ children }: { children: ReactNode }): JSX.Element {
  return (
    <>
      <FixedNav chips={[]} />
      <main className="mx-auto max-w-[1280px] px-6 py-8 flex flex-col gap-4">
        <Link
          to="/game-autopsy"
          className="text-caption uppercase text-ink-300 hover:text-paper-50 transition-all duration-150"
        >
          ← Toutes les parties
        </Link>
        <p className="text-ink-300 italic">{children}</p>
      </main>
    </>
  );
}

export function CrossOriginBanner({ onDismiss }: { onDismiss: () => void }): JSX.Element {
  return (
    <div
      role="status"
      data-testid="autopsy-csr-banner"
      className="fixed top-0 left-0 right-0 z-30 px-4 py-2 flex items-center justify-center gap-3 font-mono text-xs bg-amber-500/[0.08] border-b border-amber-500/[0.25] text-amber-500"
    >
      <span aria-hidden="true">⚠</span>
      <span className="text-paper-50">
        Multi-thread désactivé, analyse plus lente.{' '}
        <span className="font-mono text-[11px] text-ink-300">↳ crossOriginIsolated=false</span>
      </span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Fermer le banner"
        className="ml-2 px-2 py-0.5 rounded border border-amber-500/40 hover:bg-amber-500/10 transition-colors duration-150 text-amber-500"
      >
        ✕
      </button>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso.replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' });
}

/**
 * Format `last_analyzed_at` MySQL UTC ("2026-05-20 12:34:56") en label
 * lisible local français pour le sous-titre « analyse persistée · … ».
 */
function formatRestoredAt(raw: string): string {
  const d = new Date(raw.replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
